import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Loader } from "@mantine/core";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import { updateBusinessProfile } from "../../../../../services/profile";
import { showError, showSuccess } from "../../../../../utils";
import { uploadFileToS3Api } from "../../../../../utils/PresignedUrlFunction";
import SettingsSelect from "../SettingsSelect";
import ProgramsSection from "./ProgramsSection";

// Top-tier ("Gold") plans that unlock the Design / white-label (Advanced Design) area.
const TOP_TIER_PLANS = ["agency", "gold", "scale"];

/**
 * OrganizationPanel — redesign "Organization" settings panel (admin only).
 *
 * Holds the organization-level record (BusinessProfile):
 *   - Basics:    companyName (Display name), businessEmail (read-only), contactPhone
 *   - Provider defaults: providerName, ein, ssn, npi, taxonomy, billingId
 *       (programs inherit these unless they override — see ProgramsSection)
 *   - Address:   line1 / city / state / postal_code / country (org default address)
 *   - Localization: industry (live); timezone / date / language still Coming soon
 *
 * Below the org card, ProgramsSection lists each Program (facility) with its own
 * Provider Information + address, able to inherit these org-level defaults.
 */

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
].map((s) => ({ value: s, label: s }));

const ChevronDown = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/* A card section with a clickable header that expands/collapses its body. */
function CollapsibleSection({ title, subtitle, open, onToggle, children }) {
  return (
    <div className="card-section">
      <button
        type="button"
        className="sec-collapse-head"
        onClick={onToggle}
        aria-expanded={open}
      >
        <div>
          <div className="card-title">{title}</div>
          {subtitle ? <div className="card-subtitle">{subtitle}</div> : null}
        </div>
        <span className={`sec-chev${open ? " open" : ""}`}>
          <ChevronDown />
        </span>
      </button>
      {open ? <div style={{ marginTop: 16 }}>{children}</div> : null}
    </div>
  );
}

export default function OrganizationPanel({ profile, role }) {
  const navigate = useNavigate();
  const reduxProfile = useSelector((state) => state?.profile);
  const businessProfile = profile || reduxProfile?.profile;
  const saveProfileLoading = reduxProfile?.saveProfileLoading;

  const business = businessProfile?.business || {};

  // Admin gate — Organization is admin-only (type !== "staff").
  const userType = businessProfile?.user?.type ?? role;
  const isAdmin = userType !== "staff";

  // "Advanced Design" (white-label) is a Gold-plan feature; the button is
  // inactive otherwise.
  const subscription = businessProfile?.subscription;
  const isGold =
    subscription?.isActive === true &&
    TOP_TIER_PLANS.includes((subscription?.packageId?.name || "").toLowerCase());

  // ── Live, backend-wired org fields ──
  const [companyName, setCompanyName] = useState(business?.companyName || "");
  const [contactPhone, setContactPhone] = useState(business?.contactPhone || "");

  // Provider defaults (org-level)
  const [providerName, setProviderName] = useState(business?.providerName || "");
  const [ein, setEin] = useState(business?.ein || "");
  const [ssn, setSsn] = useState(business?.ssn || "");
  const [npi, setNpi] = useState(business?.npi || "");
  const [taxonomy, setTaxonomy] = useState(business?.taxonomy || "");
  const [billingId, setBillingId] = useState(business?.billingId || "");

  // Address (org default)
  const [line1, setLine1] = useState(business?.line1 || "");
  const [city, setCity] = useState(business?.city || "");
  const [stateVal, setStateVal] = useState(business?.state || "");
  const [postalCode, setPostalCode] = useState(business?.postal_code || "");
  const [country, setCountry] = useState(business?.country || "US");

  const [businessEmail, setBusinessEmail] = useState(business?.businessEmail || "");

  const [saving, setSaving] = useState(false);
  const [openProvider, setOpenProvider] = useState(false);
  const [openLocal, setOpenLocal] = useState(false);

  // Business logo (uploaded/stored like the user profile photo).
  const logoRef = useRef();
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [logoRemoved, setLogoRemoved] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoSrc = selectedLogo?.uploadURL || (logoRemoved ? "" : business?.logo) || "";

  const handleLogoChange = async (files) => {
    const file = files?.[0];
    if (!file) return;
    try {
      setLogoUploading(true);
      // Unique key — a shared filename would make two businesses overwrite
      // each other's logo in S3.
      const result = await uploadFileToS3Api({
        file,
        unique: true,
        keyPrefix: "logo",
      });
      setSelectedLogo(result);
      setLogoRemoved(false);
    } catch (err) {
      showError(err?.message || "Logo upload failed");
    } finally {
      setLogoUploading(false);
    }
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!companyName?.trim()) {
      showError("Display name is required");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        businessEmail,
        companyName,
        contactPhone,
        logo: selectedLogo?.uploadURL || (logoRemoved ? "" : business?.logo) || "",
        // provider defaults
        providerName,
        ein,
        ssn,
        npi,
        taxonomy,
        billingId,
        // address
        line1,
        city,
        state: stateVal,
        postal_code: postalCode,
        country,
      };
      await updateBusinessProfile(payload);
      showSuccess("Organization details saved");
    } catch (err) {
      showError(err?.message || "Could not save organization details");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setCompanyName(business?.companyName || "");
    setContactPhone(business?.contactPhone || "");
    setBusinessEmail(business?.businessEmail || "");
    setSelectedLogo(null);
    setLogoRemoved(false);
    setProviderName(business?.providerName || "");
    setEin(business?.ein || "");
    setSsn(business?.ssn || "");
    setNpi(business?.npi || "");
    setTaxonomy(business?.taxonomy || "");
    setBillingId(business?.billingId || "");
    setLine1(business?.line1 || "");
    setCity(business?.city || "");
    setStateVal(business?.state || "");
    setPostalCode(business?.postal_code || "");
    setCountry(business?.country || "US");
  };

  if (!isAdmin) {
    return (
      <div className="settings-panel active" data-panel="organization">
        <div className="page-header">
          <div>
            <div className="page-title">Organization</div>
            <div className="page-subtitle">
              Your organization's information and branding.
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-section">
            <div className="card-subtitle">
              Only administrators can view and edit organization settings.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const comingSoon = (
    <span className="badge badge-neutral" style={{ marginLeft: 8 }}>
      Coming soon
    </span>
  );
  const disabledFieldStyle = { opacity: 0.55 };

  return (
    <div className="settings-panel active" data-panel="organization">
      <div className="page-header">
        <div>
          <div className="page-title">Organization</div>
          <div className="page-subtitle">
            Your organization's information, provider defaults, and programs.
          </div>
        </div>
        <div className="page-actions">
          <button
            type="button"
            className="btn"
            onClick={handleCancel}
            disabled={saving || saveProfileLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={handleSave}
            disabled={saving || saveProfileLoading}
          >
            {saving || saveProfileLoading ? (
              <Loader size="xs" color="#171717" />
            ) : (
              "Save changes"
            )}
          </button>
        </div>
      </div>

      <form className="card" onSubmit={handleSave}>
        {/* ── Profile ── */}
        <div className="card-section">
          <div className="card-title">Business Profile</div>
          <div className="card-subtitle" style={{ marginBottom: 16 }}>
            Used on invoices, claims, and the Daily Digest.
          </div>

          {/* Business logo */}
          <div className="org-logo-block">
            <Dropzone
              openRef={logoRef}
              onDrop={handleLogoChange}
              accept={[MIME_TYPES.png, MIME_TYPES.jpeg, MIME_TYPES.svg, MIME_TYPES.webp]}
              multiple={false}
              styles={{ root: { border: "none", padding: 0, background: "transparent" } }}
            >
              <div className="org-logo">
                {logoUploading ? (
                  <Loader size="sm" color="#FFBA45" />
                ) : logoSrc ? (
                  <img src={logoSrc} alt="Business logo" />
                ) : (
                  <span className="org-logo-empty">Logo</span>
                )}
                <span className="org-logo-cam" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                </span>
              </div>
            </Dropzone>
            <div className="org-logo-info">
              <div className="org-logo-title">Business logo</div>
              <div className="org-logo-hint">
                PNG, JPG or SVG. Shown on invoices, the Daily Digest, and across the app.
              </div>
              <div className="org-logo-actions">
                <button type="button" className="btn sm" onClick={() => logoRef.current?.()} disabled={logoUploading}>
                  {logoSrc ? "Change logo" : "Upload logo"}
                </button>
                {logoSrc ? (
                  <button
                    type="button"
                    className="btn sm profile-remove-btn"
                    onClick={() => {
                      setSelectedLogo(null);
                      // Removing an already-saved logo is persisted on Save.
                      if (business?.logo) setLogoRemoved(true);
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      <line x1="10" y1="11" x2="10" y2="17" />
                      <line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                    Remove logo
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn sm ghost-accent"
                  disabled={!isGold}
                  onClick={() => isGold && navigate("/home/settings?tab=white-label")}
                  title={isGold ? "Open Design (logo, colors, themes)" : "Advanced Design is available on the Gold plan"}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2 9.5 8.5 3 11l6.5 2.5L12 20l2.5-6.5L21 11l-6.5-2.5z" />
                  </svg>
                  Advanced Design
                </button>
              </div>
            </div>
          </div>

          <div className="form-grid org-basics">
            <div>
              <label className="label">Business name</label>
              <input
                className="input"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Business name"
              />
            </div>
            <div>
              <label className="label">Email</label>
              <input
                className="input"
                value={businessEmail}
                onChange={(e) => setBusinessEmail(e.target.value)}
                placeholder="billing@yourcompany.com"
              />
            </div>
            <div>
              <label className="label">Phone</label>
              <input
                className="input"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="+1 (555) 000-0000"
              />
            </div>
          </div>
        </div>

        {/* ── Address ── (directly under Basics) */}
        <div className="card-section">
          <div className="card-title">Address</div>
          <div className="card-subtitle" style={{ marginBottom: 12 }}>
            Programs can use this address or set their own.
          </div>
          <div className="form-grid two-col">
            <div style={{ gridColumn: "1 / -1" }}>
              <label className="label">Street</label>
              <input
                className="input"
                value={line1}
                onChange={(e) => setLine1(e.target.value)}
                placeholder="Street address"
              />
            </div>
            <div>
              <label className="label">City</label>
              <input
                className="input"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="City"
              />
            </div>
            <div>
              <label className="label">State</label>
              <SettingsSelect
                value={stateVal}
                onChange={setStateVal}
                placeholder="Select state"
                options={US_STATES}
              />
            </div>
            <div>
              <label className="label">Zip</label>
              <input
                className="input"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                placeholder="00000"
              />
            </div>
            <div>
              <label className="label">Country</label>
              <SettingsSelect
                value={country}
                onChange={setCountry}
                placeholder="Select country"
                options={[{ value: "US", label: "United States" }]}
              />
            </div>
          </div>
        </div>

        {/* ── Provider defaults (collapsible) ── */}
        <CollapsibleSection
          title="Provider defaults"
          subtitle="Default billing identity. Programs inherit these unless they set their own."
          open={openProvider}
          onToggle={() => setOpenProvider((v) => !v)}
        >
          <div className="form-grid two-col">
            <div>
              <label className="label">Provider name</label>
              <input
                className="input"
                value={providerName}
                onChange={(e) => setProviderName(e.target.value)}
                placeholder="Provider / billing entity name"
              />
            </div>
            <div>
              <label className="label">Taxonomy / Specialty</label>
              <input
                className="input"
                value={taxonomy}
                onChange={(e) => setTaxonomy(e.target.value)}
                placeholder="e.g. 251E00000X"
              />
            </div>
            <div>
              <label className="label">EIN</label>
              <input
                className="input"
                value={ein}
                onChange={(e) => setEin(e.target.value)}
                placeholder="00-0000000"
              />
            </div>
            <div>
              <label className="label">SSN</label>
              <input
                className="input"
                value={ssn}
                onChange={(e) => setSsn(e.target.value)}
                placeholder="000-00-0000"
              />
            </div>
            <div>
              <label className="label">NPI (Type 2)</label>
              <input
                className="input"
                value={npi}
                onChange={(e) => setNpi(e.target.value)}
                placeholder="10-digit NPI"
              />
            </div>
            <div>
              <label className="label">Billing Identifier (ID)</label>
              <input
                className="input"
                value={billingId}
                onChange={(e) => setBillingId(e.target.value)}
                placeholder="Billing / payer ID"
              />
            </div>
          </div>
        </CollapsibleSection>

        {/* ── Localization (collapsible — only live field rest Coming soon) ── */}
        <CollapsibleSection
          title="Localization"
          open={openLocal}
          onToggle={() => setOpenLocal((v) => !v)}
        >
          <div className="form-grid two-col">
            <div style={disabledFieldStyle}>
              <label className="label">Time zone {comingSoon}</label>
              <SettingsSelect
                disabled
                placeholder="America / Los Angeles (PT)"
                value=""
                onChange={() => {}}
                options={[]}
              />
            </div>
            <div style={disabledFieldStyle}>
              <label className="label">Date format {comingSoon}</label>
              <SettingsSelect
                disabled
                placeholder="MM / DD / YYYY"
                value=""
                onChange={() => {}}
                options={[]}
              />
            </div>
            <div style={disabledFieldStyle}>
              <label className="label">Language {comingSoon}</label>
              <SettingsSelect
                disabled
                placeholder="English"
                value=""
                onChange={() => {}}
                options={[]}
              />
            </div>
          </div>
        </CollapsibleSection>
      </form>

      <ProgramsSection
        orgAddress={{
          line1,
          city,
          state: stateVal,
          postal_code: postalCode,
        }}
        orgProvider={{ providerName, ein, ssn, npi, taxonomy, billingId }}
      />
    </div>
  );
}
